/*
 * Ce fichier contient l'ensemble des URLs de l'API et des constantes globales
 * utilisées dans l'application. Il centralise les points d'accès backend ainsi
 * que les configurations de certaines tables, afin de faciliter la maintenance
 * et d'éviter la duplication de valeurs.
 */
import {environment} from '../../../environments/environment.development';
import {TableColumn} from '../../features/dashboard/interfaces/TableColumn.interface';

const BASE_URL = environment.apiUrl;

/**
 * Ensemble des endpoints API regroupés par module fonctionnel.
 * Tous les appels HTTP doivent idéalement utiliser ces constantes.
 */
export const API_URLS = {
  AUTH: {
    LOGIN: `${BASE_URL}/auth/login`,
    REGISTER: `${BASE_URL}/auth/register`,
  },
  USERS: {
    BLOCK_USER: `${BASE_URL}/users/block-user`,
    BLOCK_CUSTOMERS_OR_HAIRDRESSERS: `${BASE_URL}/users`,
    SUSPEND_USER: `${BASE_URL}/users/suspend-user`,
    GET_USERS: `${BASE_URL}/users/all`,
    GET_ADMINS: `${BASE_URL}/users/all/admins`,
    UPDATE_ADMIN: `${BASE_URL}`,
    GET_USERS_CREATED_BY_YEAR: `${BASE_URL}/users/all/by-month`,
    GET_USERS_CREATED_BY_MONTH: `${BASE_URL}/users/all/created`,
  },
  HAIRSTYLES: {
    BASE_DEFAULT: `${BASE_URL}/hairstyles/default-hairstyle`,
    GET_ALL: `${BASE_URL}/hairstyles/default-hairstyle/all`,
    GET_CUSTOMS: `${BASE_URL}/hairstyles/custom-hairstyle/all`,
  },
  RESERVATIONS: {
    GET_ALL: `${BASE_URL}/reservation/rsv/all`,
    GET_ACCEPTED: `${BASE_URL}/reservation/accepted`,
    GET_PENDING: `${BASE_URL}/reservation/pending`,
    GET_PENDING_PAYMENT: `${BASE_URL}/reservation/attente-payment`,
    GET_PENDING_CONFIRMATION: `${BASE_URL}/reservation/attente-confirmation`,
    GET_CANCELED: `${BASE_URL}/reservation/canceled`,
    GET_FINISHED: `${BASE_URL}/reservation/finished`,
  },
  NOTIFICATIONS: {
    SUBSCRIBE: `${BASE_URL}/notifications/subscribe`,
  },
  COUPONS: {
    HANDLE_RULES: `${BASE_URL}/coupon-rules`,
    HANDLE_COUPONS: `${BASE_URL}/coupons`,
    GET_CLIENT_COUPONS: `${BASE_URL}/coupons/by-client`,
    GET_STATISTICS: `${BASE_URL}/coupons/statistics`
  },
  REFERRALS: {
    HANDLE_PROGRAM: `${BASE_URL}/referral/program`,
    GET_ALL: `${BASE_URL}/referral`,
    GET_ALL_EVENTS: `${BASE_URL}/referral/events`
  },
  VISITS: {
    VISIT_STATS: `${BASE_URL}/visit/visit-history`
  },
  BONUS: {
    HANDLE_RULES: `${BASE_URL}/bonus/bonus-rules`,
    HANDLE_BONUS: `${BASE_URL}/bonus/bonus-history`,
  },
  PROMO_CODES: {
    HANDLE_CODES: `${BASE_URL}/promo-codes`,
  },
  ANNOUNCEMENTS: {
    HANDLE_ANNOUNCEMENTS: `${BASE_URL}/annonces`,
  },
}

/**
 * Routes front-end utilisées dans le système de navigation Angular.
 */
export const APP_URLS = {
  LOGIN: '/auth/login',
  DASHBOARD: {
    BASE: '/dashboard',
    PROFILE: '/dashboard/profile',
  }
}

/**
 * Clé du localStorage contenant l'utilisateur courant.
 */
export const CURRENT_USER = "currentUser";

/**
 * Clé du localStorage contenant le token d'accès.
 */
export const ACCESS_TOKEN = "accessToken";

/**
 * Colonnes de tableau — Administrateurs
 */
export const ADMINS_TABLE_COLUMNS: TableColumn[] = [
  {key: 'name', title: 'Name', class: 'min-w-72 md:min-w-80'},
  {key: 'email', title: 'Email'},
  {key: 'created_date', title: 'Created Date'},
  {key: 'status', title: 'Status'},
  {key: 'role', title: 'Role'},
  {key: 'sex', title: 'Sex'},
  {key: 'actions', title: 'Actions', class: 'w-24 text-center'},
];

/**
 * Colonnes de tableau — Clients
 */
export const CUSTOMERS_TABLE_COLUMNS: TableColumn[] = [
  {key: 'name', title: 'Name', class: 'w-80'},
  {key: 'email', title: 'Email', class: 'w-64'},
  {key: 'address', title: 'Address', class: 'w-48'},
  {key: 'phone', title: 'Phone'},
  {key: 'sex', title: 'Sex', class: 'w-10'},
  {key: 'created_date', title: 'Joined Date', class: 'w-44'},
  {key: 'status', title: 'Status'},
  {key: 'actions', title: 'Actions', class: 'w-24 text-center'},
];

/**
 * Colonnes de tableau — Coiffeurs
 */
export const HAIRDRESSERS_TABLE_COLUMNS: TableColumn[] = [
  {key: 'name', title: 'Name', class: 'w-80'},
  {key: 'email', title: 'Email', class: 'w-64'},
  {key: 'address', title: 'Address', class: 'w-48'},
  {key: 'activity_area', title: 'Activity Area', class: 'w-48'},
  {key: 'phone', title: 'Phone', class: 'w-52'},
  {key: 'sex', title: 'Sex', class: 'w-10'},
  {key: 'created_date', title: 'Joined Date', class: 'w-36'},
  {key: 'status', title: 'Status'},
  {key: 'actions', title: 'Actions', class: 'w-24 text-center'},
];

/**
 * Colonnes de tableau — Règles de coupons
 */
export const COUPON_RULES_TABLE_COLUMNS: TableColumn[] =[
  {key: 'id', title: 'ID', class: 'w-36'},
  {key: 'description', title: 'Description', class: 'w-36'},
  {key: 'pointsThreshold', title: 'Points threshold', class: 'w-36'},
  {key: 'discountType', title: 'Discount type', class: 'w-36'},
  {key: 'discountValue', title: 'Discount value', class: 'w-36'},
  {key: 'minOrderAmount', title: 'Min order', class: 'w-36'},
  {key: 'maxDiscountAmount', title: 'Max discount', class: 'w-36'},
  {key: 'validityDays', title: 'Valid for', class: 'w-36'},
  {key: 'isActive', title: 'Status'},
  {key: 'actions', title: 'Actions', class: 'w-24 text-center'},
];

/**
 * Colonnes de tableau — Coupons
 */
export const COUPON_TABLE_COLUMNS: TableColumn[] = [
  {key: 'code', title: 'Code', class: 'w-36'},
  {key: 'discountType', title: 'Discount type', class: 'w-36'},
  {key: 'discountValue', title: 'Discount value', class: 'w-36'},
  {key: 'clientId', title: 'Client ID', class: 'w-36'},
  {key: 'couponRuleId', title: 'Coupon rule ID', class: 'w-36'},
  {key: 'validFrom', title: 'Valid from', class: 'w-36'},
  {key: 'validUntil', title: 'Expires on', class: 'w-36'},
  {key: 'reservationId', title: 'Reservation ID', class: 'w-36'},
  {key: 'createdAt', title: 'Created At', class: 'w-36'},
  {key: 'isUsed', title: 'Status'},
  {key: 'actions', title: 'Actions', class: 'w-24 text-center'},
];

/**
 * Colonnes de tableau — Parrainages
 */
export const REFERRAL_TABLE_COLUMNS: TableColumn[] = [
  {key: 'id', title: 'ID', class: 'w-36'},
  {key: 'code', title: 'Code', class: 'w-36'},
  {key: 'sponsorClientId', title: 'Sponsor Client ID', class: 'w-36'},
  {key: 'referredClientId', title: 'Referred Client ID', class: 'w-36'},
  {key: 'usesCount', title: 'Uses Count', class: 'w-36'},
  {key: 'maxUses', title: 'Max Uses', class: 'w-36'},
  {key: 'createdAt', title: 'Created At', class: 'w-36'},
  {key: 'status', title: 'Status', class: 'w-36'},
];

/**
 * Colonnes de tableau — Événements de parrainage
 */
export const REFERRAL_EVENTS_TABLE_COLUMNS: TableColumn[] = [
  {key: 'id', title: 'ID', class: 'w-36'},
  {key: 'referralId', title: 'Referral ID', class: 'w-36'},
  {key: 'eventType', title: 'Event Type', class: 'w-36'},
  {key: 'sponsorClientId', title: 'Sponsor Client ID', class: 'w-36'},
  {key: 'referredClientId', title: 'Referred Client ID', class: 'w-36'},
  {key: 'pointsAwarded', title: 'Points Awarded', class: 'w-36'},
  {key: 'createdAt', title: 'Created At', class: 'w-36'},
];

/**
 * Colonnes de tableau — Bonus
 */
export const BONUS_TABLE_COLUMNS: TableColumn[] = [
  {key: 'id', title: 'ID', class: 'w-36'},
  {key: 'type', title: 'Bonus Type', class: 'w-36'},
  {key: 'createdDate', title: 'Created At', class: 'w-36'},
  {key: 'clientId', title: 'Client ID', class: 'w-36'},
  {key: 'bonusAction', title: 'Bonus Action', class: 'w-36'},
  {key: 'amountPoints', title: 'Amount Points', class: 'w-36'},
  {key: 'description', title: 'Description', class: 'w-36'},
];

/**
 * Colonnes de tableau — Règles de bonus
 */
export const BONUS_RULES_TABLE_COLUMNS: TableColumn[] =[
  {key: 'id', title: 'ID', class: 'w-36'},
  {key: 'createdDate', title: 'Created At', class: 'w-36'},
  {key: 'action', title: 'Rule Action', class: 'w-36'},
  {key: 'amountPoints', title: 'Amount Points', class: 'w-36'},
  {key: 'description', title: 'Description', class: 'w-36'},
  {key: 'isActive', title: 'Status'},
  {key: 'actions', title: 'Actions', class: 'w-24 text-center'},
];

/**
 * Colonnes de tableau — Codes de promo
 */
export const PROMO_CODES_TABLE_COLUMNS: TableColumn[] =[
  {key: 'id', title: 'ID', class: 'w-36'},
  {key: 'code', title: 'Code', class: 'w-36'},
  {key: 'name', title: 'Name', class: 'w-36'},
  {key: 'description', title: 'Description', class: 'w-36'},
  {key: 'discountType', title: 'Discount Type', class: 'w-36'},
  {key: 'discountValue', title: 'Discount Value', class: 'w-36'},
  {key: 'minOrderAmount', title: 'Min Order Amount', class: 'w-36'},
  {key: 'maxDiscountAmount', title: 'Max Discount Amount', class: 'w-36'},
  {key: 'salonId', title: 'Salon ID', class: 'w-36'},
  {key: 'maxUses', title: 'Max Uses', class: 'w-36'},
  {key: 'currentUses', title: 'Current Uses', class: 'w-36'},
  {key: 'allowMultipleUsesPerClient', title: 'Allow Multiple Uses', class: 'w-36'},
  {key: 'validFrom', title: 'Valid From', class: 'w-36'},
  {key: 'validUntil', title: 'Valid Until', class: 'w-36'},
  {key: 'createdBy', title: 'Created By', class: 'w-36'},
  {key: 'isActive', title: 'Status'},
  {key: 'actions', title: 'Actions', class: 'w-24 text-center'},
];

export const ANNOUNCEMENTS_TABLE_COLUMNS: TableColumn[] =[
  {key: 'id', title: 'ID', class: 'w-36'},
  {key: 'title', title: 'Title', class: 'w-36'},
  {key: 'content', title: 'Content', class: 'w-36'},
  {key: 'endMessage', title: 'End Message', class: 'w-36'},
  {key: 'createdDate', title: 'Created On', class: 'w-36'},
  {key: 'isActive', title: 'Status'},
  {key: 'actions', title: 'Actions', class: 'w-24 text-center'},
];